import {
    type ExternalAccessId,
    externalAccessIdZodSchema,
} from "@@/shared/acquire/access/external-access-id";
import {
    type ResourceId,
    resourceIdZodSchema,
} from "@@/shared/acquire/resources/resource-id";
import {
    type IsoAndUnixTimestamp,
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import {
    type ProviderId,
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import { type Branded } from "@@/utilities/branded";
import { genericId } from "@@/utilities/generic-id";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    type ProductId,
    productIdZodSchema,
} from "../../../products/product-id";

// Define the SetExternalAccessResourcesId type
type SetExternalAccessResourcesId = Branded<
    string,
    "SetExternalAccessResourcesId"
>;

const {
    factory: setExternalAccessResourcesIdFactory,
    zodSchema: setExternalAccessResourcesIdZodSchema,
} = genericId<SetExternalAccessResourcesId, "set_res__">("set_res__");

type SetExternalAccessResources = {
    _id: string;
    _createdAt: IsoAndUnixTimestamp;
    _type: "SET_EXTERNAL_ACCESS_RESOURCES_COMMAND";
    providerId: ProviderId;
    externalAccessId: ExternalAccessId;
    productIds: ProductId[];
    resourceIds: ResourceId[];
};

// Define factory params
type FactoryParams = Except<
    SetExternalAccessResources,
    "_id" | "_type" | "_createdAt"
>;

// Create factory function
const setExternalAccessResourcesFactory = (params: FactoryParams) => {
    const command: SetExternalAccessResources = {
        ...params,
        _id: setExternalAccessResourcesIdFactory(),
        _type: "SET_EXTERNAL_ACCESS_RESOURCES_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

// Create Zod schema for validation
const setExternalAccessResourcesZodSchema: z.ZodSchema<SetExternalAccessResources> =
    z.object({
        _id: setExternalAccessResourcesIdZodSchema,
        _type: z.literal("SET_EXTERNAL_ACCESS_RESOURCES_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        providerId: providerIdZodSchema,
        externalAccessId: externalAccessIdZodSchema,
        productIds: z.array(productIdZodSchema),
        resourceIds: z.array(resourceIdZodSchema),
    });

export {
    setExternalAccessResourcesFactory,
    setExternalAccessResourcesZodSchema,
};
export type { SetExternalAccessResources, SetExternalAccessResourcesId };
